import { useTranslation } from "react-i18next";
import { Dialog, DialogTitle, DialogContent, DialogActions, Typography, useTheme } from "@mui/material";
import { Button, FoodsIcon } from "@foodpilot/foods";
import { Loading } from "src/components/utils/Loading";

type EditorLeaveConfirmationProps = {
    open: boolean;
    onCancel: () => void;
    onConfirm: () => void;
    onSaveAndQuit: () => Promise<void>;
    isSaving: boolean;
};

export const EditorLeaveConfirmation = (props: EditorLeaveConfirmationProps) => {
    const { t } = useTranslation();
    const { open, onCancel, onConfirm, onSaveAndQuit, isSaving } = props;
    const theme = useTheme();

    return (
        <Dialog
            open={open}
            onClose={isSaving ? undefined : onCancel}
            maxWidth="sm"
            fullWidth
            disableEscapeKeyDown={isSaving}
            sx={{
                "& .MuiDialog-paper": {
                    borderRadius: theme.spacing(2),
                },
            }}
        >
            <DialogTitle>
                <Typography variant="body1" fontSize={18} fontWeight={theme.typography.fontWeightMedium}>
                    {t("cms.editor.quit.confirm.title", "Unsaved Changes")}
                </Typography>
            </DialogTitle>
            <DialogContent>
                <Typography>
                    {t("cms.editor.quit.confirm.description", "You have unsaved changes. What would you like to do?")}
                </Typography>
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={onCancel}
                    variant="ternary"
                    disabled={isSaving}
                    startIcon={<FoodsIcon icon="close" size={2} />}
                >
                    {t("cms.editor.quit.confirm.cancel", "Continue Editing")}
                </Button>
                <Button
                    onClick={onConfirm}
                    variant="ternary"
                    disabled={isSaving}
                    startIcon={<FoodsIcon icon="delete" size={2} />}
                    sx={{
                        color: theme.palette.error.main,
                    }}
                >
                    {t("cms.editor.quit.confirm.leave", "Leave Without Saving")}
                </Button>
                <Button
                    onClick={onSaveAndQuit}
                    variant="primary"
                    disabled={isSaving}
                    startIcon={isSaving ? <Loading size={16} /> : <FoodsIcon icon="save" size={2} />}
                >
                    {t("cms.editor.quit.confirm.saveandquit", "Save & Quit")}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
